/**
 * ConversationModal — componente universal de histórico de conversa.
 *
 * Usado por:
 *  - TabConversas (agente): modo chat, com telefone no header e auto-refresh
 *  - ExecModal: modo execução, com tag "execução atual" e botões de mídia
 */

import { Fragment, useState } from "react";
import { DialogPanel } from "./DialogPanel";
import { XIcon } from "./Icons";
import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { api } from "../../lib/api";
import type { CurrentUser } from "../../lib/auth";
import { ToolCall, ToolCallPopup } from "./ToolCallPopup";
import { ExecModal, type ExecRow } from "./ExecModal";
import { backdropClose } from "./backdrop";

const _authedRoute = getRouteApi("/_authed");

// ── helpers ───────────────────────────────────────────────────────────────────

export function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

export function fmtPhone(raw: string) {
  const d = raw.replace(/\D/g, "");
  if (d.length === 13 && d.startsWith("55")) {
    const ddd = d.slice(2, 4);
    const num = d.slice(4);
    return `(${ddd}) ${num.slice(0, 5)}-${num.slice(5)}`;
  }
  return raw;
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
}

function dayLabel(iso: string) {
  const key = (d: Date) => d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const day = key(new Date(iso));
  if (day === key(new Date())) return "Hoje";
  if (day === key(new Date(Date.now() - 864e5))) return "Ontem";
  return day;
}

// ponytail: rabisco inline bem leve; trocar por asset oficial se pedirem fidelidade
const WA_DOODLES = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cg fill='none' stroke='%23DDD5C8' stroke-width='1.3' opacity='.6'%3E%3Ccircle cx='26' cy='32' r='7'/%3E%3Cpath d='M132 18l4 8 9 2-6 6 1 9-8-4-8 4 1-9-6-6 9-2z'/%3E%3Cpath d='M36 128q11-14 22 0t22 0'/%3E%3Ccircle cx='142' cy='128' r='5'/%3E%3Cpath d='M86 66l7 7m0-7l-7 7'/%3E%3Cpath d='M150 70q6-8 12 0'/%3E%3C/g%3E%3C/svg%3E")`;

// ── types ─────────────────────────────────────────────────────────────────────

interface MsgRow {
  id: string;
  role: string;
  content: string;
  tokens_in: number;
  tokens_out: number;
  media_urls: MediaItem[] | null;
  tool_names: string[] | null;
  execution_id: string | null;
  tool_calls: ToolCall[] | null;
  exec_type: string | null;
  created_at: string;
}

export interface MediaItem {
  url: string;
  type: string;
}

const MEDIA_BTN_LABELS: Record<string, string> = {
  // chaves longas (rota /media, logs antigos) + curtas (envelope v2)
  audioMessage: "Áudio", videoMessage: "Vídeo",
  imageMessage: "Foto", stickerMessage: "Figurinha", documentMessage: "Documento",
  audio: "Áudio", video: "Vídeo", image: "Foto", sticker: "Figurinha", document: "Documento",
  call_recording: "Gravação da Ligação",
};

// Caixa de entidade do lead — replica o box da WABA (LeadSearch).
function LeadField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <span style={{ color: "var(--ink-muted)", minWidth: 64 }}>{label}:</span>
      <span style={{ color: value ? "var(--ink-secondary)" : "var(--line-max)", wordBreak: "break-all" }}>{value || "—"}</span>
    </div>
  );
}
function fmtLeadTime(iso: string | null | undefined) {
  if (!iso) return "—";
  const utc = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
  return new Date(utc).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
}

// ── props ─────────────────────────────────────────────────────────────────────

export interface ConversationModalProps {
  agentId: string;
  convId: string;
  onClose: () => void;
  /** Exibe telefone e datas no header (modo TabConversas) */
  phone?: string;
  createdAt?: string;
  lastMessageAt?: string;
  /** Entidade do lead (waba_leads) — habilita o "expandir" no header */
  bsuid?: string | null;
  lead?: {
    name: string | null;
    username: string | null;
    ctwa: string | null;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
  };
  /** Estado do followup exibido ao lado do telefone (modo TabConversas) */
  followup?: {
    count: number | null;
    finalizado: boolean | null;
    motivo: string | null;
    em: string | null;
  };
  /** Auto-refresh em ms (ex: 8000). Omitir para sem refresh. */
  autoRefreshMs?: number;
  /** Contexto de execução: marca última msg do usuário como "execução atual".
   *  contextCount = nº de mensagens enviadas ao agente (para o divisor de contexto). */
  execContext?: { contextCount?: number };
  /** URL override para buscar as mensagens (ex: conversas do Playground, que
   *  vivem num endpoint separado). Se omitido, usa o endpoint padrão de produção. */
  messagesUrl?: string;
  /** Visual estilo WhatsApp (aba Conversas — prints para cliente). */
  variant?: "whatsapp";
}

// ── ConversationModal ─────────────────────────────────────────────────────────

export function ConversationModal({
  agentId, convId, onClose,
  phone, createdAt, lastMessageAt,
  bsuid, lead,
  followup,
  autoRefreshMs,
  execContext,
  messagesUrl,
  variant,
}: ConversationModalProps) {
  const wa = variant === "whatsapp";
  const [toolPopup, setToolPopup] = useState<ToolCall | null>(null);
  const [execOpen, setExecOpen] = useState<ExecRow | null>(null);
  const [execMissing, setExecMissing] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const isViewer = !!(_authedRoute.useRouteContext() as { user?: CurrentUser }).user?.permissions?.viewer;

  async function openExecution(execId: string) {
    setExecMissing(false);
    try {
      const ex = await api.get<ExecRow>(`/agents/${agentId}/executions/${execId}`);
      setExecOpen(ex);
    } catch {
      setExecMissing(true);
    }
  }

  const { data: messages = [], isLoading } = useQuery<MsgRow[]>({
    queryKey: ["conv-messages", messagesUrl ?? convId],
    queryFn: () => api.get(messagesUrl ?? `/agents/${agentId}/conversations/${convId}/messages`),
    refetchInterval: autoRefreshMs ?? false,
  });

  // media_urls vem do banco junto com cada mensagem — não precisa de lógica extra

  // No modo execução: marca a última msg do usuário e oculta a resposta da IA desta execução
  const lastUserIdx = execContext
    ? messages.reduce((acc, m, i) => (m.role === "user" || m.role === "user_patioo") ? i : acc, -1)
    : -1;
  const displayMessages = execContext && lastUserIdx >= 0
    ? messages.slice(0, lastUserIdx + 1)
    : messages;

  // Divisor de contexto: as últimas `contextCount` msgs foram enviadas ao agente.
  // Só no modo execução e quando há histórico acima do que coube no contexto.
  const contextCount = execContext?.contextCount ?? 0;
  const ctxDividerIdx = contextCount > 0 ? displayMessages.length - contextCount : -1;
  const showCtxDivider = ctxDividerIdx > 0;

  // Abrir a execução substitui esta view pelo ExecModal (evita modais sobrepostos)
  if (execOpen) {
    return (
      <ExecModal
        exec={execOpen}
        stepsUrl={`/agents/${agentId}/executions/${execOpen.id}/steps`}
        onClose={() => setExecOpen(null)}
        nested
      />
    );
  }

  return (
    <>
    {toolPopup && <ToolCallPopup tc={toolPopup} onClose={() => setToolPopup(null)} />}
    <div
      {...backdropClose(() => { onClose(); })}
      style={{
        position: "fixed", inset: 0, background: "var(--overlay)",
        zIndex: "calc(var(--z-modal) + 100)" as any, display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <DialogPanel onClose={() => onClose()} label={phone ? fmtPhone(phone) : "Histórico da conversa"} style={{
        background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 12,
        width: 600, maxHeight: "85vh", display: "flex", flexDirection: "column",
        boxShadow: "var(--shadow-modal)",
        animation: "convModalIn 0.16s ease",
      }}>
        <style>{`@keyframes convModalIn{from{opacity:0;transform:scale(0.96) translateY(6px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>

        {/* header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: "1px solid var(--line)", flexShrink: 0,
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {phone ? (
              <>
                <span style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                  {fmtPhone(phone)}
                  {followup && followup.count !== null && (
                    followup.finalizado ? (
                      <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 99, background: "var(--surface-hover)", color: "var(--ink-muted)" }}>
                        ✓ finalizado
                        {followup.motivo && ` · ${followup.motivo}`}
                        {followup.em && ` · ${fmtDate(followup.em)}`}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 99, background: "rgba(108,71,255,0.15)", color: "var(--brand)" }}>
                        {followup.count}× follow-up
                      </span>
                    )
                  )}
                </span>
                <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>
                  Criado em {fmtDate(createdAt ?? null)}
                  {lastMessageAt && ` · Última msg ${fmtDate(lastMessageAt)}`}
                </span>
              </>
            ) : (
              <span style={{ fontSize: 14, fontWeight: 600 }}>Histórico da conversa</span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {(bsuid || lead) && (
              <button
                onClick={() => setLeadOpen(v => !v)}
                title="Entidade do lead"
                style={{ color: leadOpen ? "var(--ink)" : "var(--ink-muted)", padding: 4, borderRadius: 4, display: "flex", background: "none", border: "none", cursor: "pointer" }}
                onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")}
                onMouseOut={e => (e.currentTarget.style.color = leadOpen ? "var(--ink)" : "var(--ink-muted)")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: leadOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              style={{ color: "var(--ink-muted)", padding: 4, borderRadius: 4, display: "flex", background: "none", border: "none", cursor: "pointer" }}
              onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")}
              onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Caixa de entidade do lead — igual à da WABA */}
        {leadOpen && (bsuid || lead) && (
          <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--line)", background: "var(--surface-hover)", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
              1º contato {fmtLeadTime(lead?.firstSeenAt)} · último inbound {fmtLeadTime(lead?.lastSeenAt)}
            </div>
            <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 3 }}>
              <LeadField label="bsuid" value={bsuid} />
              <LeadField label="telefone" value={phone} />
              <LeadField label="ctwa" value={lead?.ctwa} />
              <LeadField label="username" value={lead?.username} />
            </div>
          </div>
        )}

        {/* messages */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: wa ? 5 : 10,
          ...(wa ? { backgroundColor: "#EFEAE2", backgroundImage: WA_DOODLES, borderRadius: "0 0 12px 12px" } : {}),
        }}>
          {execMissing && (
            <div style={{ background: "var(--danger-soft)", border: "1px solid var(--danger-line)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "var(--danger)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span>Essa execução não existe mais (provavelmente expirou na retenção de logs).</span>
              <button aria-label="Fechar" onClick={() => setExecMissing(false)} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 14 }}><XIcon size={13} /></button>
            </div>
          )}
          {isLoading && <div style={{ color: "var(--ink-dim)", fontSize: 13 }}>Carregando mensagens...</div>}
          {!isLoading && displayMessages.length === 0 && (
            <div style={{ color: "var(--ink-dim)", fontSize: 13 }}>Nenhuma mensagem ainda.</div>
          )}

          {displayMessages.map((msg, idx) => {
            // IA externa (n8n via API): mesmo visual verde da IA, com tag própria.
            const isExtAI = msg.role === "ia_externa";
            const isAI = msg.role === "assistant" || isExtAI;
            const isHuman = msg.role === "human";
            // Humano 2ª+ durante o bloqueio: balão à direita, cinza (a 1ª segue amarela).
            const isHumanBlock = msg.role === "human_block";
            // Ligação atendida (interação humana por telefone) — resumo do subagente
            // + gravação no media_urls; lado da bolha depende de quem iniciou.
            const isCallAnswered = msg.role === "call_in" || msg.role === "call_out";
            const isCallBiz = msg.role === "call_out" || msg.role === "call_evt_out";
            const isCallEvent = msg.role === "call_evt_in" || msg.role === "call_evt_out";
            // Botão do CRM (ligar/desligar IA): bolha rosa à direita; não entra no contexto do LLM.
            const isCrmIa = msg.role === "crm_ia";
            // Lado direito (visual "empresa"): humano, IA, ou ligação/tentativa iniciada pela empresa.
            // Aviso do follow-up (janela fechada → follow consumido sem envio): só
            // histórico, o LLM nunca vê. Bolha roxa do follow-up, texto em itálico.
            const isFollowupEvt = msg.role === "followup_evt";
            const isBizSide = isAI || isHuman || isHumanBlock || isCallBiz || isCrmIa || isFollowupEvt;
            const isFollowup = msg.exec_type === "followup" || isFollowupEvt;
            const isPatioo = msg.role === "user_patioo";
            const isCurrent = execContext != null && idx === lastUserIdx;

            return (
              <Fragment key={msg.id}>
                {wa && (idx === 0 || dayLabel(displayMessages[idx - 1].created_at) !== dayLabel(msg.created_at)) && (
                  <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 4px" }}>
                    <span style={{ fontSize: 11, color: "#54656F", background: "#fff", padding: "4px 12px", borderRadius: 8, boxShadow: "0 1px .5px rgba(11,20,26,0.13)" }}>
                      {dayLabel(msg.created_at)}
                    </span>
                  </div>
                )}
                {showCtxDivider && idx === ctxDividerIdx && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
                    <div style={{ flex: 1, height: 1, background: "#22c55e33" }} />
                    <span style={{ fontSize: 9, color: "var(--ok)", letterSpacing: ".06em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 99, border: "1px solid #22c55e55", background: "var(--ok-soft)", whiteSpace: "nowrap" }}>
                      ↓ contexto enviado ao agente
                    </span>
                    <div style={{ flex: 1, height: 1, background: "#22c55e33" }} />
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", alignItems: isBizSide ? "flex-end" : "flex-start", gap: 4 }}>
                <div style={wa ? {
                  maxWidth: "75%",
                  background: isCrmIa ? "#FCE7F3" : isHuman ? "#FDF3C4" : isHumanBlock ? "#E9EDEF" : isCallAnswered ? "#D6EFFF" : isCallEvent ? "#F5F5F5" : isFollowup ? "#ECE3FC" : isAI ? "#D9FDD3" : "#fff",
                  borderRadius: isBizSide ? "8px 8px 3px 8px" : "8px 8px 8px 3px",
                  padding: "6px 9px 5px 9px",
                  boxShadow: "0 1px .5px rgba(11,20,26,0.13)",
                } : {
                  maxWidth: "75%",
                  background: isCrmIa ? "var(--pink-soft)" : isHuman ? "var(--warn-soft)" : isHumanBlock ? "rgba(100,116,139,0.14)" : isCallAnswered ? "rgba(56,189,248,0.16)" : isCallEvent ? "rgba(148,163,184,0.10)" : isCurrent ? "rgba(22,163,74,0.25)" : isFollowup ? "var(--violet-soft)" : isAI ? "var(--ok-soft)" : "var(--surface-input)",
                  border: `1px solid ${isCrmIa ? "var(--pink)" : isHuman ? "#EAB30888" : isHumanBlock ? "rgba(100,116,139,0.34)" : isCallAnswered ? "rgba(56,189,248,0.45)" : isCallEvent ? "rgba(148,163,184,0.28)" : isCurrent ? "#22c55e55" : isFollowup ? "#6C47FF55" : isAI ? "var(--ok-line)" : "var(--line-strong)"}`,
                  borderRadius: isBizSide ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                  padding: "10px 14px",
                }}>
                  {/* tag "execução atual" */}
                  {isCurrent && (
                    <div style={{ fontSize: 9, color: "var(--ok)", marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      ● execução atual
                    </div>
                  )}
                  {(isHuman || isHumanBlock) && (
                    <div style={{ fontSize: 9, color: isHuman ? "var(--warn)" : "var(--ink-muted)", marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      👤 Atendimento humano
                    </div>
                  )}
                  {isCallAnswered && (
                    <div style={{ fontSize: 9, color: "#0369A1", marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      {"📞 "}{msg.role === "call_in" ? "Ligação atendida" : "Ligação realizada"}
                    </div>
                  )}
                  {isCallEvent && (
                    <div style={{ fontSize: 9, color: "var(--ink-dim)", marginBottom: 4, letterSpacing: ".04em" }}>
                      {"📞 "}{msg.content}
                    </div>
                  )}
                  {isExtAI && (
                    <div style={{ fontSize: 9, color: "var(--ok)", marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      🤖 IA externa
                    </div>
                  )}
                  {isPatioo && (
                    <div style={{ fontSize: 9, color: "var(--brand)", marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      🚘 Mensagem via Patioo
                    </div>
                  )}
                  {isCrmIa && (
                    <div style={{ fontSize: 9, color: "var(--pink)", marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      🎛 Comando CRM
                    </div>
                  )}
                  {isFollowup && (
                    <div style={{ fontSize: 9, color: "var(--violet)", marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>
                      ↻ Follow-up{isFollowupEvt ? " · não enviado" : ""}
                    </div>
                  )}

                  {/* texto da mensagem */}
                  <div style={{
                    fontSize: wa ? 13.5 : 13,
                    color: wa ? "#111B21" : isHuman ? "#8A5A0B" : isCallAnswered ? "#0369A1" : isCurrent ? "var(--ok)" : isFollowup ? "#6D28D9" : isAI ? "var(--ok)" : "var(--ink-strong)",
                    whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: wa ? 1.4 : 1.5,
                    marginBottom: wa ? 0 : 4,
                    fontStyle: isFollowupEvt ? "italic" : undefined,
                  }}>
                    {msg.content}
                  </div>

                  {/* WhatsApp: só a hora dentro do balão; meta vai pra fora */}
                  {wa && (
                    <div style={{ fontSize: 10, color: "rgba(17,27,33,0.45)", textAlign: "right", marginTop: 3, lineHeight: 1 }}>
                      {fmtTime(msg.created_at)}
                    </div>
                  )}
                  {/* rodapé: data à esquerda, botões de mídia à direita */}
                  {!wa && <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 2 }}>
                    <span style={{ fontSize: 10, color: "var(--ink-dim)", display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span>
                        {fmtDate(msg.created_at)}
                        {isAI && msg.tokens_in > 0 && (
                          <span style={{ marginLeft: 6 }}>↑{msg.tokens_in.toLocaleString()} ↓{msg.tokens_out.toLocaleString()} tk</span>
                        )}
                      </span>
                      {(isBizSide && !isCallEvent) && msg.execution_id && !isViewer && (
                        <button
                          onClick={() => openExecution(msg.execution_id!)}
                          style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, border: "1px solid var(--line-strong)", background: "var(--line)", color: "var(--ink-secondary)", cursor: "pointer" }}
                          onMouseOver={e => { e.currentTarget.style.borderColor = "var(--violet-alt)"; e.currentTarget.style.color = "var(--violet)"; }}
                          onMouseOut={e => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}
                        >
                          Ver execução
                        </button>
                      )}
                    </span>
                    {(msg.media_urls ?? []).length > 0 && (
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        {(msg.media_urls ?? []).map((item, i) => (
                          <a key={i} href={item.url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, border: "1px solid var(--line-strong)", background: "var(--line)", color: "var(--ink-secondary)", textDecoration: "none" }}
                            onMouseOver={e => { e.currentTarget.style.borderColor = "var(--violet-alt)"; e.currentTarget.style.color = "var(--violet)"; }}
                            onMouseOut={e => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}
                          >
                            {MEDIA_BTN_LABELS[item.type] ?? item.type}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>}
                </div>

                {/* WhatsApp: meta discreta fora do balão (tokens, execução, mídia) */}
                {wa && ((isAI && msg.tokens_in > 0) || ((isBizSide && !isCallEvent) && msg.execution_id && !isViewer) || (msg.media_urls ?? []).length > 0) && (
                  <div style={{
                    display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", maxWidth: "75%",
                    fontSize: 9.5, color: "#8696A0",
                    justifyContent: isBizSide ? "flex-end" : "flex-start",
                  }}>
                    {isAI && msg.tokens_in > 0 && (
                      <span>↑{msg.tokens_in.toLocaleString()} ↓{msg.tokens_out.toLocaleString()} tk</span>
                    )}
                    {(isBizSide && !isCallEvent) && msg.execution_id && !isViewer && (
                      <button
                        onClick={() => openExecution(msg.execution_id!)}
                        style={{ fontSize: 9.5, padding: "0 5px", borderRadius: 4, border: "1px solid #D1D7DB", background: "rgba(255,255,255,0.6)", color: "#8696A0", cursor: "pointer" }}
                      >
                        Ver execução
                      </button>
                    )}
                    {(msg.media_urls ?? []).map((item, i) => (
                      <a key={i} href={item.url} target="_blank" rel="noreferrer"
                        style={{ fontSize: 9.5, padding: "0 5px", borderRadius: 4, border: "1px solid #D1D7DB", background: "rgba(255,255,255,0.6)", color: "#8696A0", textDecoration: "none" }}
                      >
                        {MEDIA_BTN_LABELS[item.type] ?? item.type}
                      </a>
                    ))}
                  </div>
                )}

                {/* tags de tool calls — sempre visíveis pelo nome; clicáveis só se execution_steps ainda existe.
                    Playground não persiste tool_names (só tool_calls) — deriva os nomes dali quando ausente. */}
                {isAI && (msg.tool_names ?? (msg.tool_calls ?? []).map(tc => tc.name)).length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 4, maxWidth: "75%" }}>
                    {(msg.tool_names ?? (msg.tool_calls ?? []).map(tc => tc.name)).map((name, i) => {
                      const detail = (msg.tool_calls ?? []).find(tc => tc.name === name);
                      const clickable = !!detail;
                      return (
                        <span
                          key={i}
                          onClick={clickable ? () => setToolPopup(detail!) : undefined}
                          style={wa ? {
                            fontSize: 9.5, padding: "0 6px", borderRadius: 4,
                            border: "1px solid #D1D7DB",
                            color: "#8696A0",
                            background: "rgba(255,255,255,0.6)",
                            cursor: clickable ? "pointer" : "default",
                          } : {
                            fontSize: 11, padding: "2px 8px", borderRadius: 4,
                            border: `1px solid ${clickable ? "var(--brand)" : "var(--danger-soft-line)"}`,
                            color: clickable ? "var(--brand)" : "#D49999",
                            background: clickable ? "var(--brand-soft)" : "rgba(195,32,32,0.03)",
                            cursor: clickable ? "pointer" : "default",
                          }}
                          onMouseEnter={e => { if (clickable) e.currentTarget.style.background = wa ? "rgba(255,255,255,0.95)" : "rgba(195,32,32,0.18)"; }}
                          onMouseLeave={e => { if (clickable) e.currentTarget.style.background = wa ? "rgba(255,255,255,0.6)" : "var(--brand-soft)"; }}
                        >
                          {name}
                        </span>
                      );
                    })}
                  </div>
                )}

              </div>
              </Fragment>
            );
          })}
        </div>
      </DialogPanel>
    </div>
    </>
  );
}
